import { SafeResourceUrl } from '@angular/platform-browser';

/**
 * Represents a single language version within a chunk's 'editions' array.
 */
export interface ChunkEdition {
  createdAt: string;
  _id: string;
  content: string | any;
  type: string;
  language: string;
  codeLanguage?: string;
  __v: number;
}

/**
 * Represents an entry in a chunk's 'mains' array, mapping a language to a main edition ID.
 */
export interface MainEdition {
  language: string;
  main: string;
}

export interface ChunkAudio {
  _id: string; // language ID
  audioDurationToBeSet: boolean;
  audioFileID: string;
  duration: number | null;
  fileLink: string;
  filename: string;
  currentTime: number;
  id: string;
  __v: number;
  words?: any[];
}

/**
 * Represents the full chunk data structure as returned by the server.
 */
export interface ServerChunk {
  _id: string;
  private: boolean;
  type: string;
  alternateID?: string;
  status?: string;
  title?: string;
  editions: ChunkEdition[];
  hasAudio: boolean;
  audios: ChunkAudio[];
  tags: any[];
  main: string;
  mains: MainEdition[];
  createdAt: string;
  _creator: string;
  __v: number;
  untranslated?: boolean;
  awaitingTranslation?: boolean;
  generateVoice?: boolean;
  isReadyForIndexing?: boolean;
  isPlaceHolder?: boolean;
}

/**
 * Represents the processed Chunk object used for rendering in the component's state.
 * This is a "View Model" that combines server data with UI-specific properties.
 */
export interface Chunk {
  _id: string;
  pieceId: string;
  isPlaceHolder?: boolean;
  style?: string;
  loadAudio?: boolean;
  type: ChunkType;
  syncState?: 'pending' | 'synced' | 'failed';
  status?: string;
  title?: string;
  isPlaying?: boolean;
  videoCurrentTime?: number;
  error?: string;
  audio?: SafeResourceUrl;
  audios: ChunkAudio[];
  editions: ChunkEdition[];
  imageURL?: string;
  hasAudio?: boolean;
  hasSilentAudio?: boolean;
  audioMustEndAt?: number;
  audioDuration?: number;
  progress?: { width: string };
  currentTime?: number;
  isReadyForIndexing?: boolean;
  index: number;
  __v: number;
  [key: string]: any; // For other dynamic properties
}

export type ChunkType = 'html' | 'image' | 'video' | 'text' | 'file' | 'audio' | 'code' | 'markdown' | 'quiz' | 'latex' | 'canvas-transcript' | 'table' | 'whiteboard';
export type ChunkLanguage = 'en' | 'fr';

export const CHUNKS_WITH_AUDIO_PLAYER = ['audio', 'video'];
export const CHUNKS_WITHOUT_AUDIO_PLAYER = ['file', 'image', 'code', 'html', 'quiz', 'latex', 'canvas-transcript', 'table', 'whiteboard'];
export const SILENT_CHUNKS = ['code', 'file', 'image', 'quiz', 'latex', 'canvas-transcript', 'table', 'whiteboard'];
export const CHUNKS_TO_SKIP_AUDIO_GEN = ['file', 'image', 'video', 'code', 'audio', 'quiz', 'latex', 'canvas-transcript', 'table', 'whiteboard'];
export const CHUNK_TYPES = ['audio', 'video', 'file', 'image', 'code', 'html', 'quiz', 'latex', 'canvas-transcript', 'table', 'whiteboard'];
export const CHUNKS_ALLOWED_FOR_AUDIO_GEN = ['html'];

export interface CopyImageAndCreateChunkData {
  sourceGcsPath: string;
  contentID: string;
  insertBelowID?: string;
  insertAsFirst?: boolean;
  language: string;
}
